/**
 * ContributePage.js — Public contribution page for family members.
 * 
 * No login required. The book creator shares a link like:
 *   https://taleboundbook.com/contribute/abc123
 * 
 * Features:
 *   - Voice recording with Whisper auto-transcription
 *   - AI polish to expand/improve drafts (Claude)
 *   - Photo upload with caption
 * 
 * Props:
 *   token: string — the invite token from the URL
 */

import React from 'react';

const API_BASE = 'https://ndb4d1r8u9.execute-api.us-east-1.amazonaws.com/dev';

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;0,700;1,400&display=swap');

* { margin: 0; padding: 0; box-sizing: border-box; }

.cp-page {
  min-height: 100svh;
  background: #fbf8f1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── Header ── */
.cp-header {
  width: 100%;
  padding: 20px 32px;
  display: flex;
  justify-content: center;
  border-bottom: 1px solid rgba(15,23,42,0.06);
}
.cp-logo {
  font-family: 'Lora', serif;
  font-size: 1rem;
  letter-spacing: 4px;
  color: #3c4653;
  text-transform: uppercase;
  font-weight: 600;
}

/* ── Main content ── */
.cp-main {
  width: 100%;
  max-width: 640px;
  padding: 48px 24px 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── Invitation card ── */
.cp-invite {
  text-align: center;
  margin-bottom: 40px;
}
.cp-invite-from {
  font-family: 'Lora', serif;
  font-size: 0.7rem;
  letter-spacing: 2px;
  color: #b0a090;
  text-transform: uppercase;
  margin-bottom: 16px;
}
.cp-invite-title {
  font-family: 'Lora', serif;
  font-size: 1.8rem;
  font-weight: 400;
  color: #0f172a;
  line-height: 1.35;
  margin-bottom: 12px;
}
.cp-invite-sub {
  font-family: 'Lora', serif;
  font-size: 1rem;
  color: #3c4653;
  line-height: 1.7;
  font-style: italic;
  max-width: 480px;
  margin: 0 auto;
}
.cp-divider {
  width: 40px;
  height: 1px;
  background: #d4c4a8;
  margin: 28px auto;
}

/* ── Question prompt ── */
.cp-question {
  font-family: 'Lora', serif;
  font-size: 1.25rem;
  color: #0f172a;
  text-align: center;
  line-height: 1.6;
  font-style: italic;
  margin-bottom: 36px;
  padding: 24px;
  background: rgba(15,23,42,0.02);
  border-radius: 12px;
  border: 1px solid rgba(15,23,42,0.06);
  width: 100%;
}

/* ── Form ── */
.cp-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.cp-label {
  font-family: 'Lora', serif;
  font-size: 0.7rem;
  letter-spacing: 2px;
  color: #3c4653;
  text-transform: uppercase;
  margin-bottom: 6px;
  display: block;
}
.cp-input {
  font-family: 'Lora', serif;
  font-size: 1rem;
  color: #0f172a;
  background: #fff;
  border: 1px solid rgba(15,23,42,0.12);
  border-radius: 8px;
  padding: 12px 16px;
  width: 100%;
  outline: none;
  transition: border-color 0.2s;
}
.cp-input:focus {
  border-color: #7c2d12;
}
.cp-textarea {
  font-family: 'Lora', serif;
  font-size: 1rem;
  color: #0f172a;
  background: #fff;
  border: 1px solid rgba(15,23,42,0.12);
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  min-height: 280px;
  outline: none;
  line-height: 1.8;
  resize: vertical;
  transition: border-color 0.2s;
}
.cp-textarea:focus {
  border-color: #7c2d12;
}
.cp-textarea::placeholder {
  color: #c4b8a8;
  font-style: italic;
}

/* ── Toolbar (voice + AI) ── */
.cp-toolbar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}
.cp-tool-btn {
  font-family: 'Lora', serif;
  font-size: 0.8rem;
  color: #3c4653;
  background: #fff;
  border: 1px solid rgba(15,23,42,0.12);
  border-radius: 8px;
  padding: 10px 16px;
  min-height: 44px;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.cp-tool-btn:hover:not(:disabled) {
  border-color: #7c2d12;
  color: #0f172a;
  background: rgba(201,168,76,0.04);
}
.cp-tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cp-tool-btn--recording {
  border-color: #c04040;
  color: #c04040;
  background: rgba(192,64,64,0.04);
  animation: cp-pulse 1.5s ease-in-out infinite;
}
@keyframes cp-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(192,64,64,0.2); }
  50% { box-shadow: 0 0 0 6px rgba(192,64,64,0); }
}

/* ── Status indicator ── */
.cp-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(201,168,76,0.06);
  border-radius: 8px;
  margin-top: 4px;
}
.cp-status-dot {
  width: 8px;
  height: 8px;
  background: #7c2d12;
  border-radius: 50%;
  animation: cp-spin 0.8s linear infinite;
}
.cp-status-text {
  font-family: 'Lora', serif;
  font-size: 0.85rem;
  color: #3c4653;
  font-style: italic;
}

/* ── AI response area ── */
.cp-ai-box {
  background: rgba(201,168,76,0.04);
  border: 1px solid rgba(201,168,76,0.2);
  border-radius: 10px;
  padding: 16px;
  margin-top: 8px;
}
.cp-ai-header {
  font-family: 'Lora', serif;
  font-size: 0.7rem;
  letter-spacing: 2px;
  color: #7c2d12;
  text-transform: uppercase;
  margin-bottom: 10px;
}
.cp-ai-text {
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  color: #0f172a;
  line-height: 1.8;
  white-space: pre-wrap;
}
.cp-ai-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.cp-ai-accept {
  font-family: 'Lora', serif;
  font-size: 0.8rem;
  padding: 6px 14px;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: #0f172a;
  color: #fbf8f1;
}
.cp-ai-accept:hover { background: #0f172a; }
.cp-ai-dismiss {
  font-family: 'Lora', serif;
  font-size: 0.8rem;
  padding: 6px 14px;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s;
  background: transparent;
  color: #3c4653;
  border: 1px solid rgba(15,23,42,0.12);
}
.cp-ai-dismiss:hover { border-color: #7c2d12; }

/* ── Photo upload ── */
.cp-photo-zone {
  border: 2px dashed rgba(15,23,42,0.12);
  border-radius: 12px;
  padding: 28px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: rgba(15,23,42,0.01);
}
.cp-photo-zone:hover {
  border-color: #7c2d12;
  background: rgba(201,168,76,0.03);
}
.cp-photo-zone-label {
  font-family: 'Lora', serif;
  font-size: 0.9rem;
  color: #3c4653;
  font-style: italic;
}
.cp-photo-zone-hint {
  font-family: 'Lora', serif;
  font-size: 0.75rem;
  color: #b0a090;
  margin-top: 6px;
}
.cp-photo-preview {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 8px;
}
.cp-photo-preview img {
  width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: 8px;
  background: rgba(15,23,42,0.03);
}
.cp-photo-remove {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0,0,0,0.5);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cp-photo-caption {
  font-family: 'Lora', serif;
  font-size: 0.85rem;
  color: #0f172a;
  background: #fff;
  border: 1px solid rgba(15,23,42,0.12);
  border-radius: 8px;
  padding: 10px 14px;
  width: 100%;
  outline: none;
  margin-top: 10px;
}
.cp-photo-caption:focus {
  border-color: #7c2d12;
}

/* ── Submit button ── */
.cp-submit {
  font-family: 'Lora', serif;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: #fff;
  background: #7c2d12;
  border: none;
  border-radius: 8px;
  padding: 16px 32px;
  cursor: pointer;
  transition: all 0.25s;
  margin-top: 12px;
  width: 100%;
}
.cp-submit:hover:not(:disabled) {
  background: #b8952e;
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(201,168,76,0.25);
}
.cp-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Character count ── */
.cp-charcount {
  font-family: 'Lora', serif;
  font-size: 0.75rem;
  color: #b0a090;
  text-align: right;
  margin-top: -12px;
}

/* ── Messages ── */
.cp-error {
  font-family: 'Lora', serif;
  font-size: 0.85rem;
  color: #a04040;
  text-align: center;
  padding: 12px;
  background: rgba(160,64,64,0.05);
  border-radius: 8px;
}
.cp-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 24px;
}
.cp-spinner {
  width: 32px;
  height: 32px;
  border: 2px solid rgba(15,23,42,0.1);
  border-top-color: #7c2d12;
  border-radius: 50%;
  animation: cp-spin 0.8s linear infinite;
}
@keyframes cp-spin { to { transform: rotate(360deg); } }

/* ── Success state ── */
.cp-success {
  text-align: center;
  padding: 40px 24px;
}
.cp-success-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}
.cp-success-title {
  font-family: 'Lora', serif;
  font-size: 1.5rem;
  color: #0f172a;
  margin-bottom: 12px;
}
.cp-success-sub {
  font-family: 'Lora', serif;
  font-size: 1rem;
  color: #3c4653;
  font-style: italic;
  line-height: 1.7;
  max-width: 400px;
  margin: 0 auto 28px;
}
.cp-success-another {
  font-family: 'Lora', serif;
  font-size: 0.9rem;
  color: #7c2d12;
  background: none;
  border: 1px solid #7c2d12;
  border-radius: 8px;
  padding: 12px 24px;
  cursor: pointer;
  transition: all 0.2s;
}
.cp-success-another:hover {
  background: rgba(201,168,76,0.08);
}

/* ── Invalid / expired ── */
.cp-invalid {
  text-align: center;
  padding: 60px 24px;
}
.cp-invalid-icon {
  font-size: 2.5rem;
  margin-bottom: 16px;
  opacity: 0.4;
}
.cp-invalid-title {
  font-family: 'Lora', serif;
  font-size: 1.3rem;
  color: #0f172a;
  margin-bottom: 10px;
}
.cp-invalid-sub {
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  color: #3c4653;
  font-style: italic;
  line-height: 1.6;
}

/* ── Footer ── */
.cp-footer {
  width: 100%;
  padding: 24px 32px;
  text-align: center;
  border-top: 1px solid rgba(15,23,42,0.06);
  margin-top: auto;
}
.cp-footer-text {
  font-family: 'Lora', serif;
  font-size: 0.75rem;
  color: #b0a090;
  letter-spacing: 1px;
}
.cp-footer-link {
  color: #3c4653;
  text-decoration: none;
}
.cp-footer-link:hover {
  color: #0f172a;
}

/* ── Recording timer ── */
.cp-rec-timer {
  font-family: 'Lora', serif;
  font-size: 0.8rem;
  color: #c04040;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 640px) {
  .cp-main { padding: 32px 16px 60px; }
  .cp-invite-title { font-size: 1.4rem; }
  .cp-question { font-size: 1.1rem; padding: 20px; }
  .cp-textarea { min-height: 220px; }
}
`;

// ═══════════════════════════════════════════════════════════════

// F-73: the contribution screen's AI polish ("Clean this up" / "Rewrite beautifully") is the outlawed
// destructive-rewrite family (236c22a precedent — "Polish with AI" was removed from chapter edit) sitting on
// a grief-adjacent journey. Hidden behind a default-OFF flag pending the [CELEBRATION-001] review, same
// pattern as KID_MODE_ENABLED. Flip to true only after that review rules on it.
const AI_POLISH_ENABLED = false;

function ContributePage({ token }) {
  const [state, setState] = React.useState('loading'); // loading | form | success | invalid
  const [invite, setInvite] = React.useState(null);
  const [name, setName] = React.useState('');
  const [story, setStory] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [photo, setPhoto] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const fileInputRef = React.useRef(null);

  // ── Voice/Video Recording State ──
  const [recording, setRecording] = React.useState(false);
  const [videoMode, setVideoMode] = React.useState(false);
  const [transcribing, setTranscribing] = React.useState(false);
  const [recSeconds, setRecSeconds] = React.useState(0);
  const recorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const streamRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const videoRecorderRef = React.useRef(null);
  const videoChunksRef = React.useRef([]);
  const liveVideoRef = React.useRef(null);

  // ── AI Polish State ──
  const [aiPolishing, setAiPolishing] = React.useState(false);
  const [aiResponse, setAiResponse] = React.useState('');
  const [aiTierUsed, setAiTierUsed] = React.useState('');

  // ── API helper (direct to API Gateway — no Amplify) ──
  const callStoryAPI = async (body) => {
    const resp = await fetch(`${API_BASE}/story`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw new Error(`API error ${resp.status}`);
    return resp.json();
  };

  // Fetch invite on mount
  React.useEffect(() => {
    if (!token) { setState('invalid'); return; }
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/contribute?token=${token}`);
        // A server/network problem is NOT an expired link. Only treat confirmed
        // not-found/expired (4xx or data.expired) as invalid; 5xx/network -> retryable error.
        if (!resp.ok) { setState(resp.status >= 500 ? 'error' : 'invalid'); return; }
        const data = await resp.json();
        if (data.expired || data.error) { setState('invalid'); return; }
        setInvite(data);
        setState('form');
      } catch (err) {
        console.error('Invite fetch error:', err);
        setState('error');
      }
    })();
  }, [token]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  // ═══════════════════════════════════════════════════════
  // VOICE / VIDEO RECORDING
  // ═══════════════════════════════════════════════════════

  const startRecording = async () => {
    try {
      // Pick a MIME the browser actually supports. iOS Safari does NOT support the
      // webm container, so a hardcoded 'audio/webm' silently fails to record there.
      // Probe and prefer mp4 on Safari; fall back to webm elsewhere.
      const pickMime = (cands) => {
        if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
        for (const t of cands) { try { if (MediaRecorder.isTypeSupported(t)) return t; } catch (e) {} }
        return '';
      };
      const audioMime = pickMime(['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm']);
      if (videoMode) {
        // Video mode: capture video+audio, use audio-only for transcription
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        streamRef.current = stream;
        if (liveVideoRef.current) { liveVideoRef.current.srcObject = stream; liveVideoRef.current.play(); }
        if (!audioMime) { stream.getTracks().forEach(t => t.stop()); setError("Recording isn't supported in this browser. Please type your story instead."); return; }
        const videoMime = pickMime(['video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']);
        // Video recorder (not saved for Contribute, but enables preview)
        videoChunksRef.current = [];
        const videoRec = new MediaRecorder(stream, videoMime ? { mimeType: videoMime, audioBitsPerSecond: 48000 } : { audioBitsPerSecond: 48000 });
        videoRec.ondataavailable = (e) => { if (e.data.size > 0) videoChunksRef.current.push(e.data); };
        videoRecorderRef.current = videoRec;
        // Audio-only recorder for transcription
        const audioStream = new MediaStream(stream.getAudioTracks());
        chunksRef.current = [];
        const audioRec = new MediaRecorder(audioStream, { mimeType: audioMime, audioBitsPerSecond: 48000 });
        audioRec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
        audioRec.onstop = async () => {
          const blob = new Blob(chunksRef.current, { type: audioMime });
          stream.getTracks().forEach(t => t.stop());
          clearInterval(timerRef.current);
          timerRef.current = null;
          setRecording(false);
          await transcribeBlob(blob);
        };
        recorderRef.current = audioRec;
        videoRec.start(100);
        audioRec.start(250);
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        if (!audioMime) { stream.getTracks().forEach(t => t.stop()); setError("Recording isn't supported in this browser. Please type your story instead."); return; }
        const recorder = new MediaRecorder(stream, { mimeType: audioMime, audioBitsPerSecond: 48000 });
        chunksRef.current = [];
        recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
        recorder.onstop = async () => {
          const blob = new Blob(chunksRef.current, { type: audioMime });
          stream.getTracks().forEach(t => t.stop());
          clearInterval(timerRef.current);
          timerRef.current = null;
          setRecording(false);
          await transcribeBlob(blob);
        };
        recorderRef.current = recorder;
        recorder.start(250);
      }
      setRecording(true);
      setRecSeconds(0);
      setError('');
      timerRef.current = setInterval(() => {
        setRecSeconds(s => {
          const next = s + 1;
          if (next >= 1200) {
            setTimeout(() => stopRecording(), 0);
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      setError(videoMode
        ? 'Could not access camera. Please allow camera access and try again.'
        : 'Could not access microphone. Please allow microphone access and try again.');
    }
  };

  const stopRecording = () => {
    if (videoRecorderRef.current) {
      videoRecorderRef.current.stop();
      videoRecorderRef.current = null;
    }
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
    }
  };

  const blobToBase64 = async (blob) => {
    const arrayBuffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    const chunkSize = 8192;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const splitChunksForTranscription = (rawChunks, maxBytes = 3_670_016) => {
    if (!rawChunks || rawChunks.length === 0) return [];
    const headerChunk = rawChunks[0];
    const segments = [];
    let currentSegment = [headerChunk];
    let currentSize = headerChunk.size;
    for (let i = 1; i < rawChunks.length; i++) {
      if (currentSize + rawChunks[i].size > maxBytes && currentSegment.length > 1) {
        segments.push(new Blob(currentSegment, { type: 'audio/webm' }));
        currentSegment = [headerChunk];
        currentSize = headerChunk.size;
      }
      currentSegment.push(rawChunks[i]);
      currentSize += rawChunks[i].size;
    }
    if (currentSegment.length > 0) {
      segments.push(new Blob(currentSegment, { type: 'audio/webm' }));
    }
    return segments;
  };

  const transcribeBlob = async (blob) => {
    setTranscribing(true);
    setError('');
    try {
      // Match the filename extension to what was actually recorded (mp4 on iOS Safari,
      // webm elsewhere) so the transcription service reads the right format.
      const ext = (blob.type || '').includes('mp4') ? 'mp4' : ((blob.type || '').includes('ogg') ? 'ogg' : 'webm');
      // Split into chunks if blob is large. Only webm survives naive chunk splitting;
      // mp4 needs its container intact, so send it whole.
      let segments;
      if (blob.size > 3_670_016 && chunksRef.current.length > 1 && ext === 'webm') {
        segments = splitChunksForTranscription(chunksRef.current);
      } else {
        segments = [blob];
      }

      const results = [];
      for (const segment of segments) {
        const base64 = await blobToBase64(segment);
        const data = await callStoryAPI({
          action: 'transcribe',
          audioBase64: base64,
          filename: `recording.${ext}`,
          token, // TB-085: ties the public transcribe call to the invite for per-invite capping
        });
        if (data.error) throw new Error(data.error);
        const text = (data.text || '').trim();
        if (text) results.push(text);
      }

      const fullText = results.join(' ').trim();
      if (fullText) {
        setStory(prev => prev.trim() ? prev.trimEnd() + '\n\n' + fullText : fullText);
      } else {
        setError('Could not detect any speech. Please try again.');
      }
    } catch (err) {
      console.error('Transcription error:', err);
      setError('Transcription failed. ' + (err.message || 'Please try again.'));
    } finally {
      setTranscribing(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ═══════════════════════════════════════════════════════
  // AI POLISH
  // ═══════════════════════════════════════════════════════

  const handleAIPolish = async (tier = 'cleanup') => {
    if (!story.trim() || story.trim().length < 30) {
      setError('Write a bit more before polishing — even a rough draft is fine!');
      return;
    }
    setAiPolishing(true);
    setAiResponse('');
    setError('');
    try {
      const questionCtx = invite?.question ? `They were answering: "${invite.question}"\n\n` : '';
      const OUTPUT_RULES = `
OUTPUT RULES — CRITICAL:
- Output ONLY the text. Nothing else.
- Do NOT include any notes, commentary, explanations, or metadata
- Do NOT write things like "Note:", "I kept...", "Here's...", "Changes made:", etc.
- Do NOT include markdown headers (##), horizontal rules (---), or formatting markers
- Do NOT explain what you changed or why
- The output goes DIRECTLY into a printed book — treat it like final copy`;

      let prompt;
      if (tier === 'grammar') {
        prompt = `${questionCtx}Fix the grammar and spelling in this text for a printed keepsake book.

RULES:
- Fix spelling, punctuation, and grammar errors
- Remove filler words (um, uh, like, you know) if this came from speech
- Break the text into short paragraphs (2-4 sentences each)
- Do NOT change their words, phrasing, or sentence structure
- Do NOT add transitions, connections, or new sentences
- Do NOT expand or add detail
- Keep it in first person
- Keep all names, places, and details exactly as stated
${OUTPUT_RULES}

Their text:
${story.trim()}`;
      } else if (tier === 'cleanup') {
        prompt = `${questionCtx}Clean up this text for a printed keepsake book.

RULES:
- Fix grammar, spelling, and punctuation
- Smooth transitions between ideas
- Connect fragments into flowing paragraphs
- Remove filler words from speech
- Break the text into short paragraphs (2-4 sentences each)
- Do NOT add emotions, thoughts, or details they didn't write
- Do NOT add literary flourishes or poetic language
- Do NOT significantly expand the length
- Keep THEIR words and vocabulary
- Keep it in first person
${OUTPUT_RULES}

Their text:
${story.trim()}`;
      } else {
        prompt = `${questionCtx}Turn this person's words into a short first-person story chapter for their printed keepsake book.

RULES:
- Use THEIR actual words and phrases — restructure for flow, but don't replace their language
- It's OK to expand rough notes into full paragraphs
- It's OK to add paragraph breaks, smooth transitions, and reorder for flow
- Do NOT add emotions, thoughts, or dialogue they didn't include
- Do NOT add literary flourishes or poetic language
- Keep their specific names, places, dates, and details exactly as stated
- Match their vocabulary level
- Aim for 150-400 words
${OUTPUT_RULES}

Their words:
${story.trim()}`;
      }

      const data = await callStoryAPI({
        action: 'expand',
        roughDraft: prompt,
        token, // TB-085: ties the public expand call to the invite for per-invite capping
      });
      if (data.error) throw new Error(data.error);
      setAiResponse(data.text || '');
      setAiTierUsed(tier);
    } catch (err) {
      console.error('AI polish error:', err);
      setError('Pip could not polish your story. ' + (err.message || 'Please try again.'));
    } finally {
      setAiPolishing(false);
    }
  };

  const acceptAIResponse = () => { setStory(aiResponse); setAiResponse(''); };
  const dismissAIResponse = () => { setAiResponse(''); };

  // ═══════════════════════════════════════════════════════
  // PHOTO & SUBMIT
  // ═══════════════════════════════════════════════════════

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('Image must be under 10 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto({ file, preview: reader.result, caption: '' });
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Please enter your name'); return; }
    if (!story.trim()) { setError('Please write your story'); return; }
    if (story.trim().length < 50) { setError('Your story seems a bit short — tell us more!'); return; }
    if (invite?.contributionDeadline && new Date() > new Date(invite.contributionDeadline + 'T23:59:59')) {
      setError('The submission deadline for this book has passed. Contact the organizer if you need an extension.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      // Step 1: If there's a photo, get presigned URL and upload directly to S3
      let photoKey = null;
      if (photo?.file) {
        const urlResp = await fetch(`${API_BASE}/contribute`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'get_upload_url',
            token,
            contentType: photo.file.type || 'image/jpeg',
            filename: photo.file.name,
          }),
        });
        if (!urlResp.ok) throw new Error('Failed to prepare photo upload');
        const { uploadUrl, photoKey: key } = await urlResp.json();
        photoKey = key;

        // Upload photo directly to S3 (no base64, no Lambda payload limit)
        const uploadResp = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': photo.file.type || 'image/jpeg' },
          body: photo.file,
        });
        if (!uploadResp.ok) throw new Error('Photo upload failed');
      }

      // Step 2: Submit the story with the S3 key (not the photo data)
      const resp = await fetch(`${API_BASE}/contribute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          contributorName: name.trim(),
          title: title.trim() || '',
          content: story.trim(),
          photoKey: photoKey || null,
          photoCaption: photo?.caption || '',
        }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || 'Submission failed');
      }
      setState('success');
    } catch (err) {
      console.error('Contribute submit error:', err);
      setError('Something went wrong submitting your story. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnother = () => {
    setName(''); setStory(''); setTitle(''); setPhoto(null);
    setError(''); setAiResponse(''); setState('form');
  };

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════

  return (
    <>
      <style>{STYLES}</style>
      <div className="cp-page">
        <div className="cp-header">
          <div className="cp-logo">Talebound</div>
        </div>

        <div className="cp-main">
          {/* Loading */}
          {state === 'loading' && (
            <div className="cp-loading">
              <div className="cp-spinner" />
              <div style={{ fontFamily: "'Lora', serif", color: '#3c4653', fontSize: '0.9rem', fontStyle: 'italic' }}>
                Loading your invitation...
              </div>
            </div>
          )}

          {/* Network / server error (retryable) — distinct from an expired link */}
          {state === 'error' && (
            <div className="cp-invalid">
              <div className="cp-invalid-icon">📡</div>
              <div className="cp-invalid-title">We could not load your invitation</div>
              <div className="cp-invalid-sub">
                This is usually a connection hiccup, not a problem with your link. Please check your connection and try again.
              </div>
              <button onClick={() => window.location.reload()}
                style={{ marginTop: 18, padding: '12px 26px', borderRadius: 9999, border: 'none', background: 'linear-gradient(135deg,#F0A24C,#D2761F)', color: '#fff', fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer' }}>
                Try again
              </button>
            </div>
          )}

          {/* Invalid / expired */}
          {state === 'invalid' && (
            <div className="cp-invalid">
              <div className="cp-invalid-icon">📬</div>
              <div className="cp-invalid-title">This link has expired</div>
              <div className="cp-invalid-sub">
                Ask the person who sent you this link to generate a new one.
                Invite links expire after 30 days for your family's privacy.
              </div>
              <a href="https://taleboundbook.com"
                style={{ display: 'inline-block', marginTop: 18, fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: '1rem', color: '#9a7a3a', textDecoration: 'none' }}>
                Or start your own Talebound book →
              </a>
            </div>
          )}

          {/* Form */}
          {state === 'form' && invite && (
            <>
              <div className="cp-invite">
                {invite.occasion && invite.recipientName ? (
                  <>
                    <div className="cp-invite-from">You're contributing to a special book</div>
                    <div className="cp-invite-title">
                      {invite.occasion === 'MOTHERS_DAY' ? "A Mother's Day Book" :
                       invite.occasion === 'FATHERS_DAY' ? "A Father's Day Book" :
                       invite.occasion === 'BIRTHDAY' ? 'A Birthday Book' :
                       invite.occasion === 'GRANDPARENTS_DAY' ? 'A Grandparents Day Book' :
                       invite.occasion === 'CHRISTMAS' ? 'A Christmas Book' :
                       invite.occasion === 'ANNIVERSARY' ? 'An Anniversary Book' :
                       invite.occasion === 'WEDDING' ? 'A Wedding Book' :
                       invite.occasion === 'IN_MEMORY' ? 'A Book of Remembrance' :
                       invite.occasion === 'RETIREMENT' ? 'A Retirement Book' :
                       invite.occasion === 'GRADUATION' ? 'A Graduation Book' :
                       invite.occasion === 'VALENTINES' ? "A Valentine's Book" :
                       invite.occasion === 'NEW_BABY' ? 'A New Baby Book' :
                       invite.occasion === 'APPRECIATION' ? 'A Book of Appreciation' :
                       'A Celebration Book'} for {invite.recipientName}
                    </div>
                    {invite.message && <div className="cp-invite-sub">{invite.message}</div>}
                    {invite.contributionDeadline && (
                      <div style={{ marginTop: 12, padding: '10px 16px', background: 'rgba(201,168,76,0.06)', borderRadius: 8, display: 'inline-block' }}>
                        <span style={{ fontFamily: "'Lora', serif", fontSize: '0.9rem', color: '#3c4653' }}>
                          Submit by: <strong style={{ color: '#0f172a' }}>{new Date(invite.contributionDeadline + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="cp-invite-from">You've been invited to contribute</div>
                    <div className="cp-invite-title">
                      {invite.creatorName} is writing a family book
                    </div>
                    {invite.message && <div className="cp-invite-sub">{invite.message}</div>}
                  </>
                )}
              </div>
              <div className="cp-divider" />

              {invite.question && (
                <div className="cp-question">"{invite.question}"</div>
              )}

              <div className="cp-form">
                {/* Name + relationship */}
                <div>
                  <label className="cp-label">Your Name</label>
                  <input className="cp-input" type="text" value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder={invite.recipientName ? `e.g. Sarah, ${invite.recipientName}'s daughter` : 'e.g. Aunt Linda, Dad, Grandma Rose'} maxLength={100} />
                  {invite.occasion && (
                    <div style={{ fontSize: '0.8rem', color: '#b0a090', fontFamily: "'Lora', serif", marginTop: 4, fontStyle: 'italic' }}>
                      Include your relationship if you'd like — it will appear in the book
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="cp-label">Story Title (optional)</label>
                  <input className="cp-input" type="text" value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="e.g. The Summer We Found the Kitten" maxLength={200} />
                </div>

                {/* Story */}
                <div>
                  <label className="cp-label">Your Story</label>
                  <textarea className="cp-textarea" value={story}
                    onChange={e => setStory(e.target.value)}
                    placeholder="Just write naturally — the way you'd tell it out loud to someone you love. Or tap the microphone to speak it instead..."
                    maxLength={50000} />
                  <div className="cp-charcount">
                    {story.length > 0 ? `${story.trim().split(/\s+/).filter(Boolean).length} words` : ''}
                  </div>

                  {/* Voice / Video + AI toolbar */}
                  <div className="cp-toolbar">
                    {!recording && !transcribing && (
                      <>
                        <p style={{ fontSize: '0.85rem', color: '#3c4653', marginBottom: 6, fontStyle: 'italic' }}>Your recording is used to create a written story — the words you speak will be preserved in the book. Take your time — you can record up to 20 minutes.</p>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 10 }}>
                          <button onClick={() => setVideoMode(false)}
                            style={{ padding: '8px 20px', fontSize: '0.95rem', fontFamily: "'Lora', serif", fontWeight: 600, background: !videoMode ? 'rgba(15,23,42,0.85)' : 'rgba(15,23,42,0.06)', color: !videoMode ? '#fff' : '#3c4653', border: '1px solid rgba(15,23,42,0.15)', borderRadius: 20, cursor: 'pointer' }}>
                             Voice
                          </button>
                          <button onClick={() => setVideoMode(true)}
                            style={{ padding: '8px 20px', fontSize: '0.95rem', fontFamily: "'Lora', serif", fontWeight: 600, background: videoMode ? 'rgba(15,23,42,0.85)' : 'rgba(15,23,42,0.06)', color: videoMode ? '#fff' : '#3c4653', border: '1px solid rgba(15,23,42,0.15)', borderRadius: 20, cursor: 'pointer' }}>
                             Video
                          </button>
                        </div>
                      </>
                    )}
                    {recording && videoMode && (
                      <video ref={liveVideoRef} muted playsInline
                        style={{ width: '100%', maxWidth: 320, borderRadius: 12, marginBottom: 10, background: '#1a1612', border: '2px solid rgba(220,80,60,0.4)' }} />
                    )}
                    {!recording ? (
                      <button className="cp-tool-btn" onClick={startRecording}
                        disabled={transcribing || aiPolishing}
                        title={`Record your story with your ${videoMode ? 'camera' : 'voice'}`}>
                        {videoMode ? '' : ''} {videoMode ? 'Record Video' : 'Speak Your Story'}
                      </button>
                    ) : (
                      <button className="cp-tool-btn cp-tool-btn--recording" onClick={stopRecording}>
                        ⏹ Stop Recording <span className="cp-rec-timer" style={recSeconds >= 1080 ? { color: '#e07a5f' } : undefined}>{formatTime(recSeconds)}{recSeconds >= 1080 ? ' — wrapping up soon' : ''}</span>
                      </button>
                    )}
                  </div>

                  {/* 3-tier AI options — F-73: hidden behind AI_POLISH_ENABLED (default OFF) */}
                  {AI_POLISH_ENABLED && story.trim().length >= 30 && !aiResponse && (
                    <div style={{ marginTop: 12 }}>
                      <p style={{ fontSize: '0.85rem', color: '#3c4653', textAlign: 'center', marginBottom: 2, fontFamily: "'Lora', serif" }}>
                        {aiPolishing ? 'Working on it...' : 'Want some help with your writing?'}
                      </p>
                      {!aiPolishing && <p style={{ fontSize: '0.8rem', color: '#3c4653', textAlign: 'center', marginBottom: 8, fontStyle: 'italic' }}>Light cleanup or a full rewrite — your voice stays either way.</p>}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {[
                          { tier: 'cleanup', icon: '', label: 'Clean this up' },
                          { tier: 'chapter', icon: '', label: 'Rewrite beautifully' },
                        ].map(({ tier, icon, label }) => (
                          <button key={tier} onClick={() => handleAIPolish(tier)}
                            disabled={aiPolishing || recording || transcribing}
                            style={{
                              flex: 1, minWidth: 120, padding: '10px 12px', borderRadius: 10,
                              border: '1px solid rgba(15,23,42,0.12)', background: '#fff',
                              cursor: aiPolishing ? 'wait' : 'pointer', fontSize: '0.9rem',
                              fontFamily: "'Lora', serif", color: '#0f172a',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                              opacity: aiPolishing ? 0.6 : 1,
                            }}>
                            {aiPolishing ? '' : icon} {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Transcribing */}
                  {transcribing && (
                    <div className="cp-status">
                      <div className="cp-status-dot" />
                      <div className="cp-status-text">Transcribing your recording...</div>
                    </div>
                  )}

                  {/* AI polishing */}
                  {aiPolishing && (
                    <div className="cp-status">
                      <div className="cp-status-dot" />
                      <div className="cp-status-text">Pip is polishing your story...</div>
                    </div>
                  )}

                  {/* AI response — F-73: gated too (defense in depth; unreachable while the buttons are hidden) */}
                  {AI_POLISH_ENABLED && aiResponse && (
                    <div className="cp-ai-box">
                      <div className="cp-ai-header">
                        {aiTierUsed === 'cleanup' ? ' Cleaned Up' : ' Full Rewrite'}
                      </div>
                      <div className="cp-ai-text">{aiResponse}</div>
                      <div className="cp-ai-actions">
                        <button className="cp-ai-accept" onClick={acceptAIResponse}>Use This Version</button>
                        <button className="cp-ai-dismiss" onClick={dismissAIResponse}>Keep My Original</button>
                      </div>
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(15,23,42,0.08)', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <span style={{ fontSize: '0.8rem', color: '#3c4653', width: '100%', textAlign: 'center', marginBottom: 4 }}>Try a different style:</span>
                        {[
                          { tier: 'cleanup', icon: '', label: 'Clean up' },
                          { tier: 'chapter', icon: '', label: 'Rewrite beautifully' },
                        ].filter(({ tier }) => tier !== aiTierUsed).map(({ tier, icon, label }) => (
                          <button key={tier} onClick={() => { setAiResponse(''); handleAIPolish(tier); }}
                            disabled={aiPolishing}
                            style={{
                              padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(15,23,42,0.12)',
                              background: '#fff', cursor: 'pointer', fontSize: '0.85rem',
                              fontFamily: "'Lora', serif", color: '#3c4653',
                            }}>
                            {icon} {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Photo */}
                <div>
                  <label className="cp-label">Add a Photo (optional)</label>
                  {!photo ? (
                    <div className="cp-photo-zone" onClick={() => fileInputRef.current?.click()}>
                      <div className="cp-photo-zone-label"> Click to add a photo</div>
                      <div className="cp-photo-zone-hint">A photo that goes with your story</div>
                      <input ref={fileInputRef} type="file" accept="image/*"
                        onChange={handlePhotoSelect} style={{ display: 'none' }} />
                    </div>
                  ) : (
                    <div>
                      <div className="cp-photo-preview">
                        <img src={photo.preview} alt="Your photo" />
                        <button className="cp-photo-remove" onClick={() => setPhoto(null)}>✕</button>
                      </div>
                      <input className="cp-photo-caption" type="text" value={photo.caption}
                        onChange={e => setPhoto(prev => ({ ...prev, caption: e.target.value }))}
                        placeholder="Caption (optional) — e.g. Mom and Dad, Christmas 1987" maxLength={200} />
                    </div>
                  )}
                </div>

                {error && <div className="cp-error">{error}</div>}

                <button className="cp-submit" onClick={handleSubmit}
                  disabled={submitting || !name.trim() || !story.trim() || recording || transcribing}>
                  {submitting ? 'Adding your story...' : 'Add my story to the book'}
                </button>
              </div>
            </>
          )}

          {/* Success */}
          {state === 'success' && (
            <div className="cp-success">
              <div className="cp-success-icon"></div>
              <div className="cp-success-title">Your story has been submitted!</div>
              <div className="cp-success-sub">
                {invite?.recipientName
                  ? `Thank you for sharing. ${invite.recipientName} is going to love this. Your story is now part of the book.`
                  : 'Thank you for sharing. This means more than you know. Your story has been added to the book.'}
              </div>
              <button className="cp-success-another" onClick={handleAnother}>
                Submit another story
              </button>
              <div style={{ marginTop: 32, padding: '24px 20px', background: 'rgba(15,23,42,0.03)', borderRadius: 12, border: '1px solid rgba(15,23,42,0.08)', textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', fontFamily: "'Lora', serif", fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>
                  Want to create a book of your own?
                </div>
                <div style={{ fontSize: '0.95rem', color: '#3c4653', lineHeight: 1.5, marginBottom: 14 }}>
                  Collect stories from your family, add photos, and turn them into a beautiful hardcover keepsake.
                </div>
                <a href="https://taleboundbook.com/auth" style={{ display: 'inline-block', padding: '12px 28px', background: '#7c2d12', color: '#fff', borderRadius: 10, fontFamily: "'Lora', serif", fontSize: '1rem', textDecoration: 'none', fontWeight: 700 }}>
                  Start Your Book →
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="cp-footer">
          <div className="cp-footer-text">
            Made with <a href="https://taleboundbook.com" className="cp-footer-link">Talebound</a>. Share your story in a real book.
          </div>
        </div>
      </div>
    </>
  );
}

export default ContributePage;